import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import WorkoutMockup from './mockups/WorkoutMockup';
import { EASE } from './brand';

const TRUST = ['Invite-only accounts', 'Web, iOS & Android', 'Your club\u2019s colors'];

/** Slow-drifting light so the dark hero reads like moving water rather than a flat panel. */
function Puddle({ className, delay = 0, drift = 40 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      aria-hidden="true"
      className={`pointer-events-none absolute rounded-full blur-3xl ${className}`}
      animate={reduce ? undefined : { x: [0, drift, 0], y: [0, -drift / 2, 0], opacity: [0.5, 0.85, 0.5] }}
      transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut', delay }}
    />
  );
}

export default function Hero({ onLogin, onRequestDemo }) {
  const reduce = useReducedMotion();

  return (
    <section id="top" className="relative overflow-hidden bg-slate-950 pb-20 pt-32 sm:pb-28 sm:pt-40">
      {/* atmosphere */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,#12306b_0%,#0a1730_45%,#04070f_100%)]" />
        <div className="absolute inset-0 opacity-[0.35] [background-image:linear-gradient(to_right,rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.08)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(70%_60%_at_50%_20%,black,transparent)]" />
        <Puddle className="-left-24 top-10 h-72 w-72 bg-sky-500/20" />
        <Puddle className="right-0 top-40 h-96 w-96 bg-blue-600/20" delay={3} drift={-50} />
        <Puddle className="bottom-0 left-1/3 h-64 w-[28rem] bg-cyan-400/10" delay={6} drift={30} />
      </div>

      <div className="relative mx-auto max-w-6xl safe-area-x sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
          {/* copy */}
          <div className="text-center lg:text-left">
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-sky-200 backdrop-blur"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" />
              <span className="hidden sm:inline">
                Purpose-built for rowing &mdash; not adapted from another sport
              </span>
              <span className="sm:hidden">Purpose-built for rowing</span>
            </motion.div>

            <motion.h1
              initial={reduce ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
              className="mt-6 text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              The operating system for your{' '}
              <span className="bg-gradient-to-r from-sky-300 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
                boathouse
              </span>
              .
            </motion.h1>

            <motion.p
              initial={reduce ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
              className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slate-300 lg:mx-0"
            >
              Lineups and river conditions in the morning. Erg results scored against GMS in the
              afternoon. Every boat, blade and meter accounted for in between &mdash; for athletes,
              coxswains, coaches and club admins.
            </motion.p>

            <motion.div
              initial={reduce ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.24, ease: EASE }}
              className="mt-9 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start"
            >
              <button
                onClick={onRequestDemo}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-7 py-3.5 text-base font-semibold text-slate-900 shadow-xl shadow-blue-950/40 transition-transform hover:scale-[1.02] active:scale-95 sm:w-auto"
              >
                Request a demo
                <svg
                  className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
              <button
                onClick={onLogin}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition-colors hover:border-white/30 hover:bg-white/10 sm:w-auto"
              >
                Sign in
              </button>
            </motion.div>

            <motion.ul
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-400 lg:justify-start"
            >
              {TRUST.map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <svg className="h-3.5 w-3.5 text-sky-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {t}
                </li>
              ))}
            </motion.ul>
          </div>

          {/* product preview */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 40, rotateX: 8 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
            className="relative mx-auto w-full max-w-md lg:max-w-none"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-tr from-sky-500/20 to-indigo-500/10 blur-2xl" aria-hidden="true" />
            <motion.div
              className="relative"
              animate={reduce ? undefined : { y: [0, -10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            >
              <WorkoutMockup />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
